import { SimplePet, PET_STAGES, getNextStage, canEvolve, calculateHappiness } from '@/types/simplePet';
import { FOOD_ITEMS } from '@/types/petFood';

export const createNewPet = (userId: string, petName: string): SimplePet => {
  return {
    id: `pet_${userId}`,
    name: petName,
    emoji: PET_STAGES.baby.emoji,
    stage: 'baby',
    happiness: 100,
    health: 100,
    totalPoints: 0,
    lastInteraction: new Date(),
    createdAt: new Date(),
    portionTimestamps: [],
    pledgeSheetId: undefined,
    dailyTargetProgress: { sets: 0, openings: 0, closings: 0, closed: 0 },
    weeklyPledgeProgress: { sets: 0, openings: 0, closings: 0, closed: 0 },
    lastDailyUpdate: new Date(),
    lastWeeklyUpdate: new Date(),
    pledgePerformanceStreak: 0,
    hat: null,
    accessory: null,
    // Food & Economy System
    activityCredits: 10, // Starting credits
    hunger: 80,
    energy: 80,
  };
};

export const updatePetWithPoints = (pet: SimplePet, pointsEarned: number): SimplePet => {
  const updatedPet = {
    ...pet,
    totalPoints: pet.totalPoints + pointsEarned,
    happiness: Math.min(100, pet.happiness + Math.floor(pointsEarned / 2)), // Gain happiness from activities
    lastInteraction: new Date()
  };

  // Check for evolution
  if (canEvolve(updatedPet)) {
    const nextStage = getNextStage(updatedPet.stage);
    if (nextStage) {
      return {
        ...updatedPet,
        stage: nextStage,
        emoji: PET_STAGES[nextStage].emoji,
        happiness: 100 // Full happiness on evolution
      };
    }
  }

  return updatedPet;
};

export const updatePetHappiness = (pet: SimplePet): SimplePet => {
  return {
    ...pet,
    happiness: calculateHappiness(pet)
  };
};

export const getPetMood = (happiness: number): { emoji: string; message: string; color: string } => {
  if (happiness >= 80) return { emoji: '😊', message: 'Your pet is thriving!', color: 'text-green-500' };
  if (happiness >= 60) return { emoji: '🙂', message: 'Your pet is doing well!', color: 'text-blue-500' };
  if (happiness >= 40) return { emoji: '😐', message: 'Your pet needs attention.', color: 'text-yellow-500' };
  if (happiness >= 20) return { emoji: '😢', message: 'Your pet is feeling neglected.', color: 'text-orange-500' };
  return { emoji: '😭', message: 'Your pet needs care!', color: 'text-red-500' };
};

export const givePetAttention = (pet: SimplePet): SimplePet => {
  return {
    ...pet,
    happiness: Math.min(100, pet.happiness + 10),
    lastInteraction: new Date()
  };
};

// Portion system helpers (max 3, +1 every 8 hours)
const PORTION_MAX = 3;
const PORTION_REFILL_MS = 8 * 60 * 60 * 1000;

export const calculateSimplePetAvailablePortions = (
  pet: SimplePet
): { availablePortions: number; nextPortionTime: Date | null } => {
  const now = Date.now();
  const active = (pet.portionTimestamps ?? []).filter(
    (ts) => now - new Date(ts).getTime() < PORTION_REFILL_MS
  );
  const availablePortions = Math.max(0, PORTION_MAX - active.length);
  const nextPortionTime =
    active.length > 0
      ? new Date(
          Math.min(...active.map((ts) => new Date(ts).getTime())) +
            PORTION_REFILL_MS
        )
      : null;
  return { availablePortions, nextPortionTime };
};

export const consumePetPortion = (pet: SimplePet): SimplePet => {
  const { availablePortions } = calculateSimplePetAvailablePortions(pet);
  if (availablePortions <= 0) return pet;
  const now = new Date();
  const active = (pet.portionTimestamps ?? []).filter(
    (ts) => now.getTime() - new Date(ts).getTime() < PORTION_REFILL_MS
  );
  return {
    ...pet,
    portionTimestamps: [...active, now],
    happiness: Math.min(100, pet.happiness + 10),
    lastInteraction: now,
  };
};

// Food System Functions
export const canAffordFood = (pet: SimplePet, foodKey: string): boolean => {
  const food = FOOD_ITEMS[foodKey];
  return !!food && pet.activityCredits >= food.tokenCost;
};

export const isItemUnlocked = (pet: SimplePet, xpRequired: number): boolean => {
  return pet.totalPoints >= xpRequired;
};

export const feedPetWithFood = (pet: SimplePet, foodKey: string): SimplePet => {
  const food = FOOD_ITEMS[foodKey];
  
  if (!food || !canAffordFood(pet, foodKey) || !isItemUnlocked(pet, food.xpRequired)) {
    return pet;
  }
  
  const { availablePortions } = calculateSimplePetAvailablePortions(pet);
  if (availablePortions <= 0) {
    return pet; // No portions available
  }
  
  const now = new Date();
  const active = (pet.portionTimestamps ?? []).filter(
    (ts) => now.getTime() - new Date(ts).getTime() < PORTION_REFILL_MS
  );
  
  return {
    ...pet,
    activityCredits: pet.activityCredits - food.tokenCost,
    portionTimestamps: [...active, now], // Consume a portion
    happiness: Math.min(100, pet.happiness + food.effects.happiness),
    hunger: Math.min(100, pet.hunger + food.effects.hunger),
    energy: Math.min(100, pet.energy + (food.effects.energy || 0)),
    lastInteraction: now,
  };
};

export const earnActivityCredits = (pet: SimplePet, pointsEarned: number): SimplePet => {
  // Convert activity points to activity credits (1:1 ratio for now)
  const creditsEarned = Math.floor(pointsEarned * 0.5); // 50% conversion rate
  
  return {
    ...pet,
    activityCredits: pet.activityCredits + creditsEarned,
  };
};